import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Eye } from 'lucide-react';

import { getAsset, getAssetHistory, getAssetDocuments, addAssetDocument, deleteAssetDocument } from '../../services/assetService';
import { formatDate, formatDateTime } from '../../utils/Utils';
import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Tooltip from '../../components/ui/Tooltip';
import IconButton from '../../components/ui/IconButton';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';
import PersonCell from '../../components/ui/PersonCell';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const InfoCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
`;

const InfoLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #A0A8B4;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'available' ? '#2e7d3215' :
    $status === 'assigned' ? '#0291ef15' :
    $status === 'maintenance' ? '#ed6c0215' :
    '#9e9e9e15'};
  color: ${({ $status }) =>
    $status === 'available' ? '#2e7d32' :
    $status === 'assigned' ? '#0291ef' :
    $status === 'maintenance' ? '#ed6c02' :
    '#9e9e9e'};
`;

export default function AssetDetailsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { canWrite } = useAuth();
  const [asset, setAsset] = useState(null);
  const [activeTab, setActiveTab] = useState('history');

  // History state
  const [historyData, setHistoryData] = useState([]);
  const [historyFetching, setHistoryFetching] = useState(true);
  const [historyPagination, setHistoryPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [historyParams, setHistoryParams] = useState({ pageNumber: 1, pageSize: 10 });

  // Documents state
  const [docData, setDocData] = useState([]);
  const [docFetching, setDocFetching] = useState(true);
  const [docPagination, setDocPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [docParams, setDocParams] = useState({ pageNumber: 1, pageSize: 10 });
  const [docDrawerOpen, setDocDrawerOpen] = useState(false);
  const [docDeleteConfirm, setDocDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [docDeleting, setDocDeleting] = useState(false);
  const [docRefresh, setDocRefresh] = useState(0);
  // { fileUrl, title } | null — açık olduğunda DocumentViewerModal'ı besler. `fileUrl` KAYITLI
  // depolama adresidir (varlık belgesi artık özel sınıf — bkz. StoragePurposes.assetDocument),
  // imzalı adres DEĞİL; çözümü DocumentViewerModal kendisi `/v1/storage/view` üzerinden yapar
  // (özlük-dosyası yüzeyleriyle aynı desen, bkz. ExpenseDetailPage.js / PbDocumentsPage.js).
  const [viewing, setViewing] = useState(null);

  const lang = i18n.language;

  useEffect(() => {
    getAsset(id)
      .then((res) => setAsset(res.data?.data))
      .catch(() => {});
  }, [id]);

  // Fetch history
  useEffect(() => {
    if (activeTab !== 'history') return;
    setHistoryFetching(true);
    getAssetHistory(id, historyParams)
      .then((res) => {
        const result = res.data?.data;
        setHistoryData(result?.data || []);
        setHistoryPagination({
          totalRowCount: result?.totalRowCount || 0,
          pageIndex: (result?.pageIndex || 1) - 1,
          pageSize: result?.pageSize || 10,
        });
      })
      .finally(() => setHistoryFetching(false));
  }, [id, historyParams, activeTab]);

  // Fetch documents
  useEffect(() => {
    if (activeTab !== 'documents') return;
    setDocFetching(true);
    getAssetDocuments(id, docParams)
      .then((res) => {
        const result = res.data?.data;
        setDocData(result?.data || []);
        setDocPagination({
          totalRowCount: result?.totalRowCount || 0,
          pageIndex: (result?.pageIndex || 1) - 1,
          pageSize: result?.pageSize || 10,
        });
      })
      .finally(() => setDocFetching(false));
  }, [id, docParams, activeTab, docRefresh]);

  const statusLabels = useMemo(() => ({
    available: t('asset.available'),
    assigned: t('asset.assigned'),
    maintenance: t('asset.maintenance'),
    retired: t('asset.retired'),
  }), [t]);

  const actionTypeLabels = useMemo(() => ({
    created: t('assetHistory.created'),
    assigned: t('assetHistory.assigned'),
    returned: t('assetHistory.returned'),
    status_changed: t('assetHistory.statusChanged'),
    updated: t('assetHistory.updated'),
  }), [t]);

  const tabs = [
    { key: 'history', label: t('assetHistory.title') },
    { key: 'documents', label: t('assetDocument.title') },
  ];

  const historyColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'actionType', label: t('assetHistory.actionType'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => actionTypeLabels[v] || v,
      },
    },
    { name: 'performedByUserName', label: t('assetHistory.performedBy'), options: {
      // Public API'den yapılan işlemde işlemi yapan bir kişi değil API anahtarıdır (ad = anahtarın adı).
      customBodyRender: (v, tableMeta) => (historyData[tableMeta.rowIndex]?.performedByApiKey && v
        ? t('apiKeys.actor', { name: v })
        : <PersonCell name={v} profilePicture={historyData[tableMeta.rowIndex]?.performedByProfilePicture} />
      ), filter: false, sort: false } },
    { name: 'targetUserName', label: t('assetHistory.targetUser'), options: {
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={historyData[tableMeta.rowIndex]?.targetUserProfilePicture} />
      ), filter: false, sort: false } },
    { name: 'oldValue', label: t('assetHistory.oldValue'), options: { filter: false, sort: false } },
    { name: 'newValue', label: t('assetHistory.newValue'), options: { filter: false, sort: false } },
    { name: 'notes', label: t('assetHistory.notes'), options: { filter: false, sort: false } },
    {
      name: 'createDate', label: t('assetHistory.title'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatDateTime(v, lang),
      },
    },
  ], [t, actionTypeLabels, lang, historyData]);

  const handleHistoryTableChange = useCallback((action, tableState) => {
    setHistoryParams({
      pageNumber: (tableState.page || 0) + 1,
      pageSize: tableState.rowsPerPage || 10,
    });
  }, []);

  // Document columns
  const docTypeLabels = useMemo(() => ({
    invoice: t('assetDocument.invoice'),
    warranty: t('assetDocument.warranty'),
    contract: t('assetDocument.contract'),
    photo: t('assetDocument.photo'),
    other: t('assetDocument.other'),
  }), [t]);

  const handleDocDelete = useCallback((row) => {
    setDocDeleteConfirm({ isOpen: true, id: row.id, name: row.name });
  }, []);

  const handleDocDeleteConfirm = useCallback(async () => {
    setDocDeleting(true);
    await deleteAssetDocument(docDeleteConfirm.id);
    setDocDeleteConfirm({ isOpen: false, id: null, name: '' });
    setDocDeleting(false);
    setDocRefresh((p) => p + 1);
  }, [docDeleteConfirm.id]);

  const handleDocSave = useCallback(async (formData) => {
    try {
      await addAssetDocument(id, formData);
      setDocDrawerOpen(false);
      setDocRefresh((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, [id]);

  const docActionOptions = useMemo(() => {
    if (!canWrite('AM')) return [];
    return [
      { type: 'delete', label: t('assetDocument.delete'), onClick: handleDocDelete },
    ];
  }, [t, canWrite, handleDocDelete]);

  const docColumns = useMemo(() => {
    const cols = [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'name', label: t('assetDocument.name'), options: { filter: false, sort: false } },
      { name: 'description', label: t('assetDocument.description'), options: { filter: false, sort: false } },
      {
        name: 'documentType', label: t('assetDocument.documentType'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => docTypeLabels[v] || v || '-',
        },
      },
      {
        name: 'mediaUrl', label: t('assetDocument.mediaUrl'),
        options: {
          filter: false, sort: false,
          // Varlık belgesi ÖZEL sınıfa taşındı (StoragePurposes.assetDocument): kayıtlı adres
          // artık `files.flexbee.app` üzerinden servis edilir ve imzasız açılırsa Worker 403
          // döner — ham adresi bir <a href>'e vermek kesmeden sonra bu kolonu bütünüyle kırar.
          // Ayrıca ürün sahibi kararı: belgeler ASLA yeni sekmede açılmaz, uygulama-içi
          // görüntüleyicide açılır. `Eye`, ExternalLink DEĞİL — ExternalLink ikonu tam da
          // reddedilen davranışı vaat ediyordu (bkz. PersonnelFileView.js'in aynı notu).
          customBodyRender: (v, tableMeta) => v ? (
            <Tooltip text={t('personnelFile.openFile')}>
              <IconButton
                type="button"
                size="sm"
                aria-label={t('personnelFile.openFile')}
                onClick={() => setViewing({
                  fileUrl: v,
                  title: docData[tableMeta.rowIndex]?.name,
                })}
              >
                <Eye size={16} />
              </IconButton>
            </Tooltip>
          ) : '-',
        },
      },
    ];

    if (docActionOptions.length > 0) {
      cols.push({
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = docData[tableMeta.rowIndex];
            return <OptionsMenu actions={docActionOptions} row={row} />;
          },
        },
      });
    }

    return cols;
  }, [t, docTypeLabels, docActionOptions, docData]);

  const handleDocTableChange = useCallback((action, tableState) => {
    setDocParams({
      pageNumber: (tableState.page || 0) + 1,
      pageSize: tableState.rowsPerPage || 10,
    });
  }, []);

  const docFormSchema = useMemo(() => [
    { name: 'name', label: t('assetDocument.name'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('assetDocument.description'), type: 'textarea', grid: 12 },
    {
      name: 'mediaUrl', label: t('assetDocument.mediaUrl'), type: 'pictureUpload', grid: 12, required: true,
      accept: 'image/*,.pdf,.doc,.docx,.xls,.xlsx',
      validTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      maxSize: 10,
    },
    {
      name: 'documentType', label: t('assetDocument.documentType'), type: 'select', grid: 12,
      options: [
        { id: 'invoice', name: t('assetDocument.invoice') },
        { id: 'warranty', name: t('assetDocument.warranty') },
        { id: 'contract', name: t('assetDocument.contract') },
        { id: 'photo', name: t('assetDocument.photo') },
        { id: 'other', name: t('assetDocument.other') },
      ],
      idKey: 'id', labelKey: 'name',
    },
  ], [t]);

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('asset.title'), path: '/assets' },
          { label: asset?.name || '...' },
        ]} />

        {asset && (
          <InfoCard>
            <InfoGrid>
              <div>
                <InfoLabel>{t('asset.name')}</InfoLabel>
                <InfoValue>{asset.name}</InfoValue>
              </div>
              <div>
                <InfoLabel>{t('asset.categoryName')}</InfoLabel>
                <InfoValue>{asset.categoryName || '-'}</InfoValue>
              </div>
              <div>
                <InfoLabel>{t('asset.status')}</InfoLabel>
                <StatusBadge $status={asset.status}>{statusLabels[asset.status] || asset.status}</StatusBadge>
              </div>
              <div>
                <InfoLabel>{t('asset.brand')}</InfoLabel>
                <InfoValue>{asset.brand || '-'}</InfoValue>
              </div>
              <div>
                <InfoLabel>{t('asset.model')}</InfoLabel>
                <InfoValue>{asset.model || '-'}</InfoValue>
              </div>
              <div>
                <InfoLabel>{t('asset.barcodeNumber')}</InfoLabel>
                <InfoValue>{asset.barcodeNumber || '-'}</InfoValue>
              </div>
              <div>
                <InfoLabel>{t('asset.serialNumber')}</InfoLabel>
                <InfoValue>{asset.serialNumber || '-'}</InfoValue>
              </div>
              <div>
                <InfoLabel>{t('asset.locationName')}</InfoLabel>
                <InfoValue>{asset.locationName || '-'}</InfoValue>
              </div>
              <div>
                <InfoLabel>{t('asset.supplier')}</InfoLabel>
                <InfoValue>{asset.supplier || '-'}</InfoValue>
              </div>
              <div>
                <InfoLabel>{t('asset.purchaseDate')}</InfoLabel>
                <InfoValue>{formatDate(asset.purchaseDate, lang)}</InfoValue>
              </div>
              <div>
                <InfoLabel>{t('asset.warrantyEndDate')}</InfoLabel>
                <InfoValue>{formatDate(asset.warrantyEndDate, lang)}</InfoValue>
              </div>
              <div>
                <InfoLabel>{t('asset.maintenanceEndDate')}</InfoLabel>
                <InfoValue>{formatDate(asset.maintenanceEndDate, lang)}</InfoValue>
              </div>
            </InfoGrid>
            {asset.description && (
              <div style={{ marginTop: 16 }}>
                <InfoLabel>{t('asset.description')}</InfoLabel>
                <InfoValue>{asset.description}</InfoValue>
              </div>
            )}
          </InfoCard>
        )}

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <TabContent>
      {activeTab === 'history' && (
        <DataTable
          title={t('assetHistory.title')}
          fetching={historyFetching}
          columns={historyColumns}
          data={historyData}
          totalRowCount={historyPagination.totalRowCount}
          pageIndex={historyPagination.pageIndex}
          pageSize={historyPagination.pageSize}
          onTableChanged={handleHistoryTableChange}
        />
      )}

      {activeTab === 'documents' && (
        <>
          <GenericFormDrawer
            open={docDrawerOpen}
            onClose={() => setDocDrawerOpen(false)}
            title={t('assetDocument.add')}
            schema={docFormSchema}
            onSave={handleDocSave}
          />

          <ConfirmDialog
            isOpen={docDeleteConfirm.isOpen}
            title={t('assetDocument.delete')}
            message={t('asset.deleteMessage', { name: docDeleteConfirm.name })}
            onConfirm={handleDocDeleteConfirm}
            onCancel={() => setDocDeleteConfirm({ isOpen: false, id: null, name: '' })}
            loading={docDeleting}
          />

          <DataTable
            title={t('assetDocument.title')}
            fetching={docFetching}
            columns={docColumns}
            data={docData}
            totalRowCount={docPagination.totalRowCount}
            pageIndex={docPagination.pageIndex}
            pageSize={docPagination.pageSize}
            canCreateNewRecord={canWrite('AM')}
            onNewButtonClick={() => setDocDrawerOpen(true)}
            onTableChanged={handleDocTableChange}
          />
        </>
      )}
      </TabContent>

      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.title}
        onClose={() => setViewing(null)}
      />
    </PageContainer>
  );
}
